import React, { useState } from 'react';
import clsx from 'clsx';
import { MessageSquare, AlertCircle, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import { SessionFeedbackChart, AppNPSChart, FeedbackDetails } from '../charts';
import { EmptyValue, KPICardSkeleton } from '../ui';
import type { FeedbacksReportsResponse } from '../../types/reports';
import { processFeedbacksToWeekly } from '../../utils/feedbackReportUtils';

export interface FeedbackSectionProps {
  data: FeedbacksReportsResponse | null;
  comparisonData: FeedbacksReportsResponse | null;
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
}

/**
 * Feedback reports section component
 * Displays session feedback, NPS charts, and detailed feedback
 */
export const FeedbackSection: React.FC<FeedbackSectionProps> = ({
  data,
  comparisonData,
  loading = false,
  error = null,
  onRetry
}) => {
  const [expandedSections, setExpandedSections] = useState({
    sessionFeedback: false,
    appFeedback: false
  });

  // Process feedback into weekly data
  const processedWeeks = data ? processFeedbacksToWeekly(data) : { sessionWeeks: [], appWeeks: [] };
  const sessionWeeks = processedWeeks.sessionWeeks;

  // Calculate feedback insights
  const feedbackInsights = data ? {
    sessionAverage: data.summary.statistics.sessionFeedbacks > 0
      ? data.feedbacks
          .filter(f => f.type === 'session' && f.feedbackData.overallRating != null)
          .reduce((sum, f) => sum + (f.feedbackData.overallRating || 0), 0) / 
          data.feedbacks.filter(f => f.type === 'session' && f.feedbackData.overallRating != null).length
      : null,
    commentCount: data.feedbacks.filter(f => 
      f.feedbackData && Object.values(f.feedbackData).some(val => 
        typeof val === 'string' && val.trim().length > 0
      )
    ).length
  } : null;

  return (
    <section className="mb-10 rounded-card border border-rm-neutral-600/20 bg-white p-6 shadow-soft">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-rm-primary-700">Experience and feedback</h2>
          <p className="text-sm text-rm-neutral-600">
            Qualitative signals from sessions and app interactions.
          </p>
        </div>
        <div className="inline-flex items-center gap-2 rounded-pill bg-rm-teal-600/10 px-3 py-1 text-xs font-semibold text-rm-teal-600">
          <MessageSquare className="h-4 w-4" />
          {data?.summary.totalFeedbacks ?? 0} records
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="mt-4 rounded-card border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-5 w-5" />
              <div>
                <p className="font-semibold">Unable to load feedback data</p>
                <p className="text-xs text-red-600/80">{error}</p>
              </div>
            </div>
            {onRetry && (
              <button
                onClick={onRetry}
                disabled={loading}
                className="inline-flex items-center gap-2 rounded-card bg-white px-3 py-1.5 text-xs font-semibold text-red-600 shadow-sm transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <RefreshCw className={clsx('h-4 w-4', { 'animate-spin': loading })} />
                Retry
              </button>
            )}
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="mt-6 space-y-8">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
            {Array.from({ length: 6 }).map((_, index) => (
              <KPICardSkeleton key={index} />
            ))}
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <KPICardSkeleton />
            <KPICardSkeleton />
            <KPICardSkeleton />
          </div>
        </div>
      )}

      {/* Data State */}
      {data && !loading && !error ? (
        <div className="mt-6 space-y-8">
          {/* Summary KPIs */}
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
            {[
              { label: 'Total feedback', value: data.summary.totalFeedbacks, badge: 'bg-rm-teal-600/10 text-rm-teal-600' },
              { label: 'Session', value: data.summary.statistics.sessionFeedbacks, badge: 'bg-rm-accent-300/20 text-rm-primary-700' },
              { label: 'App', value: data.summary.statistics.appFeedbacks, badge: 'bg-emerald-100 text-emerald-700' },
              { label: 'Unique users', value: data.summary.statistics.uniqueUsers, badge: 'bg-orange-100 text-orange-700' },
              { label: 'Unique sessions', value: data.summary.statistics.uniqueSessions, badge: 'bg-rm-neutral-600/10 text-rm-neutral-600' },
              { label: 'Other', value: data.summary.statistics.otherFeedbacks, badge: 'bg-purple-100 text-purple-700' }
            ].map(({ label, value, badge }) => (
              <div key={label} className="rounded-card border border-rm-neutral-600/20 bg-white p-4 shadow-soft">
                <span className={clsx('inline-flex items-center rounded-pill px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide', badge)}>
                  {label}
                </span>
                <p className="mt-3 text-2xl font-semibold text-rm-ink-900">{value}</p>
              </div>
            ))}
          </div>

          {/* Averages */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="rounded-card border border-emerald-200 bg-emerald-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Session satisfaction</p>
              <p className="mt-2 text-2xl font-semibold text-emerald-900">
                {feedbackInsights?.sessionAverage != null ? feedbackInsights.sessionAverage.toFixed(1) : <EmptyValue size="lg" />}
                <span className="ml-1 text-sm font-normal text-emerald-700/80">/ 10</span>
              </p>
              <p className="mt-1 text-sm text-emerald-700/70">Average rating across structured session feedback.</p>
            </div>
            <div className="rounded-card border border-rm-teal-600/20 bg-rm-teal-600/10 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-rm-teal-600">Narrative richness</p>
              <p className="mt-2 text-2xl font-semibold text-rm-primary-700">
                {feedbackInsights?.commentCount ?? <EmptyValue size="lg" />}
              </p>
              <p className="mt-1 text-sm text-rm-teal-600/70">Feedback entries containing qualitative commentary.</p>
            </div>
          </div>

          {/* Charts */}
          {(sessionWeeks.length > 0 || data) && (
            <div className="space-y-6">
              {/* Session Feedback Chart */}
              {sessionWeeks.length > 0 && (
                <div className="space-y-4 rounded-card border border-rm-neutral-600/20 bg-rm-bg-50 p-4 md:p-6">
                  <div className="flex items-center gap-2 text-rm-primary-700">
                    <MessageSquare className="h-5 w-5 text-blue-500" />
                    <h3 className="text-base font-semibold">Session feedback analytics</h3>
                  </div>
                  <SessionFeedbackChart weeks={sessionWeeks} className="rounded-card border border-rm-neutral-600/20 bg-white p-4" />
                  <div className="rounded-card border border-rm-neutral-600/20 bg-white p-4 shadow-soft">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-semibold text-rm-primary-700">Detailed responses</h4>
                      <button
                        onClick={() =>
                          setExpandedSections((prev) => ({
                            ...prev,
                            sessionFeedback: !prev.sessionFeedback
                          }))
                        }
                        className="flex items-center gap-2 text-xs font-semibold text-rm-neutral-600 transition hover:text-rm-primary-700"
                      >
                        {expandedSections.sessionFeedback ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        {expandedSections.sessionFeedback ? 'Collapse' : 'Expand'}
                      </button>
                    </div>
                    {expandedSections.sessionFeedback && (
                      <div className="mt-4 border-t border-rm-neutral-600/20 pt-4">
                        <FeedbackDetails feedbacks={data.feedbacks} type="session" />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* App NPS Chart */}
              {data && (
                <div className="space-y-4 rounded-card border border-rm-neutral-600/20 bg-rm-bg-50 p-4 md:p-6">
                  <div className="flex items-center gap-2 text-rm-primary-700">
                    <MessageSquare className="h-5 w-5 text-green-500" />
                    <h3 className="text-base font-semibold">Net Promoter Score (NPS)</h3>
                  </div>
                  <AppNPSChart
                    feedbackData={data}
                    comparisonFeedbackData={comparisonData}
                    className="rounded-card border border-rm-neutral-600/20 bg-white p-4"
                  />
                  <div className="rounded-card border border-rm-neutral-600/20 bg-white p-4 shadow-soft">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-semibold text-rm-primary-700">Detailed app responses</h4>
                      <button
                        onClick={() =>
                          setExpandedSections((prev) => ({
                            ...prev,
                            appFeedback: !prev.appFeedback
                          }))
                        }
                        className="flex items-center gap-2 text-xs font-semibold text-rm-neutral-600 transition hover:text-rm-primary-700"
                      >
                        {expandedSections.appFeedback ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        {expandedSections.appFeedback ? 'Collapse' : 'Expand'}
                      </button>
                    </div>
                    {expandedSections.appFeedback && (
                      <div className="mt-4 border-t border-rm-neutral-600/20 pt-4">
                        <FeedbackDetails feedbacks={data.feedbacks} type="app" />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        !loading &&
        !error && (
          <div className="mt-8 flex flex-col items-center justify-center rounded-card border border-rm-neutral-600/20 bg-rm-bg-50 p-10 text-center">
            <MessageSquare className="h-12 w-12 text-rm-neutral-600/50" />
            <h3 className="mt-4 text-lg font-semibold text-rm-primary-700">No feedback data</h3>
            <p className="mt-2 max-w-md text-sm text-rm-neutral-600">
              No feedback has been collected for the selected period.
            </p>
          </div>
        )
      )}
    </section>
  );
};
